/*
 * !!!! Warning !!!!
 *
 * This component is used for both public and non-public versions of the
 * local pros page. Changes will impact both, considerations must be made
 * with both versions of this page in mind!
 */

/**
 * @typedef {import ('./MarketplaceProps').default} Props
 * @typedef {import ('./MarketplaceState').default} State
 * @typedef {import ('./Offer').default} Offer
 * @typedef {import ('./Property').default} Property
 * @typedef {import('../shared/MarketplaceService/Vendor').default} Vendor
 * @typedef {import('../shared/MovingTask').default} MovingTask
 * @typedef {import('../shared/AuthProvider/State').default} AuthState
 */
import { AuthContext } from '../shared/AuthProvider';
import { translate } from '../App/Internationalization';
import { withHomeownerService } from '../service-container';
import { withSettings } from '../shared/SettingProvider/SettingProvider';
import { withTheme } from '../shared/ThemeProvider';
import * as taskService from '../shared/task-service';
import captureError from '../utils/captureError';
import ExternalAnchor from '../shared/ExternalAnchor';
import images from '../config/local/images';
import Loading from '../shared/Loading';
import Metadata from '../shared/Metadata';
import NoContent from '../shared/NoContent';
import OfferCard from './OfferCard';
import Pagination from '../shared/Pagination';
import paths from '../config/local/paths';
import React from 'react';
import { getCategoryLabel, getCategoryValue } from './helpers';
import { occupation } from '../shared/ThemeProvider/theme-utilities';
import flags from '../shared/Flags';
import { string } from '@mooveguru/js-utilities';

/** @extends {React.Component<Props, State>} */
export class Marketplace extends React.Component {
	/** @type {AuthState} */
	context = /** @type {AuthState} */ ({});

	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			categories: [],
			category: '',
			currentPageNumber: 1,
			errorMessage: null,
			isGettingData: true,
			isGettingSearchResults: false,
			isPaginated: false,
			lastSearchTerm: null,
			lastSearchTermLabel: null,
			localOfferList: [],
			offerList: [],
			postalCodeDisabled: true,
			restricted: false,
			teamMembers: [],
			tenantOfferList: [],
			totalPages: null,
		};

		this._handleChange = this._handleChange.bind(this);
		this._handleSubmit = this._handleSubmit.bind(this);
		this.isInAppModule = this.props.match.url.startsWith(paths.app.root);
		this.searchMarketplace = this.searchMarketplace.bind(this);
		this.setCurrentPageNumber = this.setCurrentPageNumber.bind(this);
	}

	/** @type {Set<string>} */
	#groups = new Set();
	#skipNextPageFetch = false;

	/**
	 * @returns {string[]}
	 */
	#getPropertyPostalCodes() {
		return Array.from(
			new Set(
				[
					...(this.props.property?.propertyList ?? []).map(
						(property) => property?.address?.postalCode ?? ''
					),
					this.props.property?.selectedProperty?.address
						?.postalCode ?? '',
				].filter(Boolean)
			)
		);
	}

	/** @param {number} pageNumber */
	setCurrentPageNumber(pageNumber) {
		this.setState({ currentPageNumber: pageNumber });
	}

	/**
	 * @returns {Promise<void>}
	 */
	async componentDidMount() {
		const routeCategory = this.props.location?.state?.category;
		const normalizedRouteCategory = routeCategory
			? getCategoryValue(routeCategory) ?? routeCategory
			: '';

		if (this.isInAppModule) {
			/*
			 * Fetch user data and team in parallel. Using allSettled to ensure
			 * one failure doesn't prevent the other from completing
			 */
			const results = await Promise.allSettled([
				this.getUserData(),
				this.#getHomeownerTeam(),
			]);

			// Handle individual failures without blocking the component
			results.forEach((result) => {
				if (result.status === 'rejected') {
					captureError(result.reason);
				}
			});
		} else {
			this.setState(
				{
					category: normalizedRouteCategory,
					isGettingData: false,
					postalCodeDisabled: false,
				},
				() =>
					this.searchMarketplace(
						normalizedRouteCategory,
						'',
						this.state.currentPageNumber
					)
			);
			return;
		}

		if (normalizedRouteCategory) {
			this.setState({
				category: normalizedRouteCategory,
			});
		}
	}

	/**
	 * @param {Props} prevProps
	 * @param {State} prevState
	 * @returns {Promise<void>}
	 */
	async componentDidUpdate(prevProps, prevState) {
		if (this.state.currentPageNumber !== prevState.currentPageNumber) {
			if (this.#skipNextPageFetch) {
				this.#skipNextPageFetch = false;
				return;
			}

			const postalCode = this.isInAppModule
				? this.props.property?.selectedProperty?.address?.postalCode ??
				  ''
				: '';

			await this.searchMarketplace(
				this.state.lastSearchTerm ?? '',
				postalCode,
				this.state.currentPageNumber
			);
		}
	}

	/**
	 * @param {MovingTask | null} nextTask
	 * @returns {Promise<void>}
	 */
	async getInitialMarketplaceOffers(nextTask) {
		const postalCode =
			this.props.property.selectedProperty?.address?.postalCode ?? '';

		if (this.props.location?.state?.category) {
			const routeCategory = this.props.location.state.category;
			const normalizedCategory =
				getCategoryValue(routeCategory) ?? routeCategory;

			this.setState({ category: normalizedCategory });

			await this.searchMarketplace(normalizedCategory, postalCode);
		} else if (nextTask?.searchTerm) {
			const normalizedCategory =
				getCategoryValue(nextTask.searchTerm) ?? nextTask.searchTerm;
			this.setState({ category: normalizedCategory });

			await this.searchMarketplace(normalizedCategory, postalCode);
		} else {
			// Load all vendors without category filter on initial page load
			await this.searchMarketplace('', postalCode);
		}
	}

	/**
	 * @returns {Promise<void>}
	 */
	async getUserData() {
		/**
		 * @type {MovingTask | null}
		 */
		let nextTask = null;

		try {
			const accessToken = this.context.accessToken;
			if (!accessToken) {
				this.setState({ isGettingData: false });
				return;
			}

			nextTask = await taskService.getNextMovingTask(accessToken);

			if (!this.props.property.propertyList) {
				this.setState({
					errorMessage: translate(
						'homeowner.pages.finances.view.error'
					),
				});

				return;
			}
		} catch (error) {
			captureError(error);
		}

		this.setState({ isGettingData: false }, () =>
			this.getInitialMarketplaceOffers(nextTask)
		);
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #getHomeownerTeam() {
		const teamMembers =
			await this.props.homeownerService.getHomeownerTeam();

		this.setState({
			teamMembers,
		});
	}

	/**
	 * @param {string} query
	 * @param {string} postalCode
	 * @param {number} [pageNumber]
	 * @returns {Promise<void>}
	 */
	async searchMarketplace(
		query,
		postalCode,
		pageNumber = this.state.currentPageNumber
	) {
		this.setState({ isGettingSearchResults: true });

		try {
			const slug = this.props.match?.params?.whiteLabelSlug ?? '';

			const result = await this.props.marketplaceService.getVendors(
				query,
				postalCode,
				pageNumber,
				slug
			);

			if (
				pageNumber > 1 &&
				result.vendors.length < 1 &&
				result.pages > 0
			) {
				this.setState({ currentPageNumber: 1 });
				await this.searchMarketplace(query, postalCode, 1);
				return;
			}

			/** @type {Vendor[]} */
			let tenantVendors = [];
			/** @type {string[]} */
			let tenantCategories = [];
			try {
				const tenantResult =
					await this.props.marketplaceService.getVendors(
						query,
						postalCode,
						1,
						slug,
						{
							limit: 50,
							relatedType: 'tenant',
						}
					);
				tenantCategories = tenantResult.categories;
				tenantVendors = tenantResult.vendors;
			} catch (error) {
				// Tenant strip is non-blocking for local pros listing.
				captureError(error);
			}

			/** @type {Vendor[]} */
			let localVendors = [];
			/** @type {string[]} */
			let localCategories = [];
			try {
				const propertyPostalCodes = this.#getPropertyPostalCodes();
				const localPostalCodes = propertyPostalCodes.length
					? propertyPostalCodes
					: [postalCode];
				const localResults = await Promise.allSettled(
					localPostalCodes.map((currentPostalCode) =>
						this.props.marketplaceService.getVendors(
							query,
							currentPostalCode,
							1,
							slug,
							{
								limit: 50,
								relatedType: 'local',
							}
						)
					)
				);
				const fulfilledLocalResults = localResults.flatMap((result) => {
					if (result.status === 'fulfilled') {
						return [result.value];
					}

					captureError(result.reason);
					return [];
				});

				localCategories = Array.from(
					new Set(
						fulfilledLocalResults.flatMap(
							(result) => result.categories ?? []
						)
					)
				);
				localVendors = Array.from(
					new Map(
						fulfilledLocalResults
							.flatMap((result) => result.vendors)
							.map((vendor) => [vendor.id, vendor])
					).values()
				);
			} catch (error) {
				captureError(error);
			}

			const categories = Array.from(
				new Set([
					...(result.categories ?? []),
					...tenantCategories,
					...localCategories,
				])
			).sort();

			this.setState((prevState) => ({
				...prevState,
				categories,
				errorMessage: null,
				isGettingSearchResults: false,
				isPaginated: result.pages > 1,
				lastSearchTerm: query,
				lastSearchTermLabel: getCategoryLabel(query) ?? query,
				localOfferList: localVendors,
				offerList: result.vendors,
				restricted: result.restricted,
				tenantOfferList: tenantVendors,
				totalPages: result.pages,
			}));
		} catch (error) {
			captureError(error);

			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
				isGettingSearchResults: false,
			});
		}
	}

	/**
	 * @protected
	 * @param {{ postalCode: string }} values
	 * @returns {Promise<void>}
	 */
	async _handleSubmit(values) {
		const propertyZipCode =
			this.props.property?.selectedProperty?.address?.postalCode;

		await this.searchMarketplace(
			this.state.category,
			propertyZipCode || values.postalCode
		);
	}

	/**
	 * @protected
	 * @param {React.ChangeEvent<HTMLSelectElement>} event
	 * @returns {void}
	 */
	_handleChange(event) {
		const postalCode =
			this.props.property?.selectedProperty?.address?.postalCode ?? '';
		const category = event?.target?.value ?? '';

		if (this.state.currentPageNumber !== 1) {
			this.#skipNextPageFetch = true;
		}

		this.setState(
			{
				category,
				currentPageNumber: 1,
			},
			() => this.searchMarketplace(category, postalCode, 1)
		);
	}

	/**
	 * @returns {JSX.Element}
	 */
	#renderNoContent() {
		if (!this.state.lastSearchTerm && !this.state.categories.length) {
			return (
				<NoContent
					message={translate(
						'global.local_pros_search.no_query.message'
					)}
					title={translate('global.local_pros_search.no_query.title')}
				/>
			);
		}

		if (!this.state.restricted) {
			return (
				<NoContent
					message={translate(
						'global.local_pros_search.no_content.message',
						this.state.lastSearchTermLabel ?? ''
					)}
					title={translate(
						'global.local_pros_search.no_content.title'
					)}
				/>
			);
		}

		const job = occupation(this.props.theme.agentOccupation);

		return (
			<NoContent
				message={translate(
					'global.local_pros_search.no_content_restricted.message',
					this.state.lastSearchTermLabel ?? '',
					job
				)}
				title={translate(
					'global.local_pros_search.no_content_restricted.title',
					job
				)}
			/>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderCategoryFilter() {
		if (!this.state.categories.length) {
			return null;
		}

		return (
			<section className="marketplace-category-filter">
				<fieldset className="input-group marketplace-category-filter__group">
					<label
						className="input-label"
						htmlFor="marketplace-category"
					>
						{translate('agent.pages.offers.view.select_category')}
					</label>
					<div className="input-stylized">
						<select
							className="input"
							id="marketplace-category"
							name="category"
							onChange={this._handleChange}
							value={this.state.category}
						>
							<option value="">
								{translate(
									'agent.pages.offers.view.all_categories'
								)}
							</option>
							{this.state.categories.map((category) => (
								<option key={category} value={category}>
									{getCategoryLabel(category) ?? category}
								</option>
							))}
						</select>
					</div>
				</fieldset>
			</section>
		);
	}

	/**
	 * @returns {JSX.Element | JSX.Element[] | null}
	 */
	#renderOffers() {
		const recommendedOffers = this.#getRecommendedOffers();
		const groupOffers = this.#getGroupOffers();

		if (recommendedOffers.length < 1 && groupOffers.length < 1) {
			if (
				this.#getTenantVendors().length > 0 ||
				this.#getLocalVendors().length > 0
			) {
				return null;
			}

			return this.#renderNoContent();
		}

		return (
			<React.Fragment>
				{this.#renderOfferSection(
					translate(
						'global.marketplace.sections.agent',
						this.#getAgentFirstName()
					),
					recommendedOffers
				)}
				{this.#renderOfferSection(
					translate(
						'global.marketplace.sections.group',
						this.#getPrimaryGroupName()
					),
					groupOffers
				)}
			</React.Fragment>
		);
	}

	/**
	 * @returns {Vendor[]}
	 */
	#getTenantVendors() {
		return this.state.tenantOfferList.filter(
			(vendor) => !!vendor && vendor.related?.type === 'tenant'
		);
	}

	/**
	 * @returns {Vendor[]}
	 */
	#getLocalVendors() {
		const propertyPostalCodes = new Set(this.#getPropertyPostalCodes());

		if (propertyPostalCodes.size < 1) {
			return this.state.localOfferList.filter(
				(vendor) => !!vendor && vendor.related?.type === 'local'
			);
		}

		return this.state.localOfferList.filter((vendor) => {
			return (
				vendor &&
				vendor.related?.type === 'local' &&
				typeof vendor.postalCode === 'string' &&
				propertyPostalCodes.has(vendor.postalCode)
			);
		});
	}

	/**
	 * @returns {Vendor[]}
	 */
	#getLocalProsOffers() {
		return this.state.offerList.filter(
			(vendor) =>
				!!vendor &&
				vendor.related?.type !== 'tenant' &&
				vendor.related?.type !== 'local'
		);
	}

	/**
	 * @returns {Vendor[]}
	 */
	#getRecommendedOffers() {
		return this.#getLocalProsOffers().filter((offer) =>
			this.#isRecommended(offer.related ?? null)
		);
	}

	/**
	 * @returns {Vendor[]}
	 */
	#getGroupOffers() {
		const officeOffers = this.#getLocalProsOffers().filter(
			(offer) => offer.related?.type === 'office'
		);
		return officeOffers.filter(
			(offer) => offer.related?.id === this.props.theme.agentGroup?.id
		);
	}

	/**
	 * @returns {string}
	 */
	#getAgentFirstName() {
		const name = this.props.theme.agentName?.trim();

		if (!name) {
			return translate('global.marketplace.sections.default_agent');
		}

		return name.split(/\s+/)[0];
	}

	/**
	 * @returns {string}
	 */
	#getPrimaryGroupName() {
		return (
			this.props.theme.agentGroup?.name ??
			translate('global.marketplace.sections.default_group')
		);
	}

	/**
	 * @param {string} title
	 * @param {Vendor[]} offers
	 * @returns {JSX.Element | null}
	 */
	#renderOfferSection(title, offers) {
		if (offers.length < 1) {
			return null;
		}

		return (
			<section className="mt-6">
				<h2 className="hl-ms-6">{title}</h2>
				<section className="mt-6 grid gap-6 sm:grid-cols-2">
					{offers.map((offer) => (
						<OfferCard
							headline={offer.headline}
							isRecommended={this.#isRecommended(
								offer.related ?? null
							)}
							key={offer.id}
							logoUrl={offer.logoUrl}
							name={offer.name}
							occupation={this.props.theme.agentOccupation}
							phoneNumber={this.#getPhoneNumber(
								offer.related ?? null,
								offer.phoneNumber ?? ''
							)}
							websiteUrl={offer.websiteUrl}
						/>
					))}
				</section>
			</section>
		);
	}

	/**
	 * @param {Vendor[]} tenantVendors
	 * @returns {Vendor[][]}
	 */
	#createTenantVendorRows(tenantVendors) {
		/** @type {Vendor[][]} */
		const rows = [];
		let index = 0;

		while (index < tenantVendors.length) {
			const remaining = tenantVendors.length - index;

			if (remaining === 4) {
				rows.push(tenantVendors.slice(index, index + 2));
				rows.push(tenantVendors.slice(index + 2, index + 4));
				break;
			}

			if (remaining === 5) {
				rows.push(tenantVendors.slice(index, index + 2));
				rows.push(tenantVendors.slice(index + 2, index + 5));
				break;
			}

			const rowSize = Math.min(3, remaining);
			rows.push(tenantVendors.slice(index, index + rowSize));
			index += rowSize;
		}

		return rows;
	}

	/**
	 * @param {Vendor} vendor
	 * @returns {JSX.Element}
	 */
	#renderTenantVendorCard(vendor) {
		const headline = string.truncate(vendor.headline || vendor.name, 45);

		const content = (
			<div className="marketplace-tenant-card">
				<div className="marketplace-tenant-card__logo-shell">
					<img
						alt={`The ${vendor.name} logo`}
						className="marketplace-tenant-card__logo"
						src={
							vendor.logoUrl || images.marketplace.logoPlaceholder
						}
					/>
				</div>
				<p className="marketplace-tenant-card__headline">{headline}</p>
			</div>
		);

		if (!vendor.websiteUrl) {
			return (
				<div className="marketplace-tenant-card-link" key={vendor.id}>
					{content}
				</div>
			);
		}

		return (
			<ExternalAnchor
				className="marketplace-tenant-card-link"
				dataActivityClick="marketplace_click"
				dataActivityId={vendor.name}
				href={vendor.websiteUrl}
				key={vendor.id}
				renderSpan={false}
			>
				{content}
			</ExternalAnchor>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderTenantVendorHeadlines() {
		const tenantVendors = [...this.#getTenantVendors()];

		if (tenantVendors.length < 1) {
			return null;
		}

		const rows = this.#createTenantVendorRows(tenantVendors);

		return (
			<section className="mt-6">
				<h1 className="hl-ms-6 marketplace-header__title mb-5">
					{this.props.theme.appTitle ?? 'HomeKeepr'} National Pros
				</h1>
				<div className="marketplace-tenant-rows">
					{rows.map((row, index) => {
						const rowStyle = {
							'--tenant-row-columns': row.length,
							'--tenant-row-columns-tablet': Math.min(
								row.length,
								2
							),
						};

						return (
							<div
								className="marketplace-tenant-row"
								key={`tenant-row-${index + 1}`}
								// @ts-ignore -- CSS custom properties are supported at runtime.
								style={rowStyle}
							>
								{row.map((vendor) =>
									this.#renderTenantVendorCard(vendor)
								)}
							</div>
						);
					})}
				</div>
				<hr className="mt-6 border-grey-100" />
			</section>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderLocalVendorHeadlines() {
		const LocalVendors = [...this.#getLocalVendors()];

		if (LocalVendors.length < 1) {
			return null;
		}

		const rows = this.#createTenantVendorRows(LocalVendors);

		return (
			<section className="marketplace-local-vendors mt-6">
				<h1 className="hl-ms-6 marketplace-header__title marketplace-local-vendors__title mb-5">
					{this.props.theme.appTitle} Local Pros
				</h1>
				<div className="marketplace-tenant-rows">
					{rows.map((row, index) => {
						const rowStyle = {
							'--tenant-row-columns': row.length,
							'--tenant-row-columns-tablet': Math.min(
								row.length,
								2
							),
						};

						return (
							<div
								className="marketplace-tenant-row"
								key={`tenant-row-${index + 1}`}
								// @ts-ignore -- CSS custom properties are supported at runtime.
								style={rowStyle}
							>
								{row.map((vendor) =>
									this.#renderTenantVendorCard(vendor)
								)}
							</div>
						);
					})}
				</div>
				<hr className="mt-6 border-grey-100" />
			</section>
		);
	}

	/**
	 * @param {{ id: string | null, type: string } | null} related
	 * @returns {boolean}
	 */
	#isRecommended(related) {
		if (!related) {
			return false;
		}

		const isAgentRecommendation =
			related.type === 'agent' && related.id === this.props.theme.agentId;

		if (!this.isInAppModule) {
			return related.type === 'agent';
		}

		if (isAgentRecommendation) {
			return this.props.settings.isEnabled(
				flags.vendor.add,
				'homeowner_tier'
			);
		}

		return false;
	}

	/**
	 * @param {{ id: string | null, type: string } | null} related
	 * @param {string} phoneNumber
	 * @returns {string}
	 */
	#getPhoneNumber(related, phoneNumber) {
		if (this.#isRecommended(related)) {
			return this.props.theme?.agentPhoneNumber ?? phoneNumber;
		}

		return phoneNumber;
	}

	render() {
		if (this.state.errorMessage) {
			return (
				<NoContent
					message={this.state.errorMessage}
					title={translate('global.error')}
				/>
			);
		}

		if (this.state.isGettingData) {
			return <Loading />;
		}

		const metadataTitle = this.props.theme?.agentName
			? `${this.props.theme.agentName}’s Marketplace`
			: 'Marketplace';
		const hasNationalPros = this.#getLocalProsOffers().length > 0;
		const hasLocalPros = this.#getLocalVendors().length > 0;
		const contentContainerClassName = this.isInAppModule
			? ''
			: 'mx-auto w-full max-w-screen-xl px-4 sm:px-6 lg:px-8';

		return (
			<React.Fragment>
				<Metadata title={metadataTitle} />
				<section className={contentContainerClassName}>
					<header className="marketplace-header">
						<h1 className="hl-ms-6 marketplace-header__title">
							Home Pros
						</h1>
						{this.#renderCategoryFilter()}
					</header>
					<hr className="mt-6 border-grey-100" />
					{this.state.isGettingSearchResults ? (
						<Loading />
					) : (
						<React.Fragment>
							{this.#renderTenantVendorHeadlines()}
							{hasLocalPros
								? this.#renderLocalVendorHeadlines()
								: null}
							{this.#renderOffers()}
						</React.Fragment>
					)}
					{!this.state.isGettingSearchResults &&
					this.state.isPaginated ? (
						<Pagination
							currentPageNumber={this.state.currentPageNumber}
							lastPageNumber={this.state.totalPages ?? 1}
							setCurrentPageNumber={this.setCurrentPageNumber}
						/>
					) : null}
					<footer className="mt-10">
						<p className="font-medium">
							{translate('global.local_pros_search.respa')}
						</p>
					</footer>
				</section>
			</React.Fragment>
		);
	}
}

// @ts-ignore
export default withHomeownerService(withTheme(withSettings(Marketplace)));

Marketplace.contextType = AuthContext;
